import { GradientContainer } from "@/components/gradient-container/GradientContainer.tsx";
import { RightPaneLayout } from "@/components/layout/RightPaneLayout.tsx";
import useFormatter from "@/hooks/use-formatter.hook.ts";
import usePermissions from "@/hooks/use-permissions.hook.ts";
import { useTitle } from "@/hooks/use-title.hook.ts";
import { EmployeeDocuments } from "@/pages/employees/employee/components/EmployeeDocuments.tsx";
import { EmployeeFeedbackEvaluation } from "@/pages/employees/employee/components/EmployeeFeedbackEvaluation.tsx";
import { EmployeeGoals } from "@/pages/employees/employee/components/EmployeeGoals.tsx";
import { EmployeeCustomTabItems } from "@/pages/employees/employee/components/EmployeeCustomTabItems.tsx";
import {
  EmployeePageSidePanelBody,
  EmployeePageSidePanelHeader,
  EmployeePageSidePanelProvider,
} from "@/pages/employees/employee/components/EmployeePageSidePanel.tsx";
import { EmployeePageHeaderSkeleton } from "@/pages/employees/employee/components/EmployeePageSkeleton.tsx";
import { EmployeePositionRequests } from "@/pages/employees/employee/components/EmployeePositionRequests.tsx";
import { EmployeeTasks } from "@/pages/employees/employee/components/EmployeeTasks.tsx";
import { EmployeeTimeOff } from "@/pages/employees/employee/components/EmployeeTimeOff.tsx";
import { EmployeePageSection } from "@/pages/employees/employee/employee-page.constants.ts";
import { GoalDetailPanel } from "@/pages/talent/goals/components/GoalDetailPanel.tsx";
import { useGoalSidePanel } from "@/pages/talent/goals/hooks/use-goal-side-panel.hook.ts";
import { useEmployeeDetails, useEmployeeMe } from "@/queries/employees/use-employees.query.ts";
import { useAppConfig } from "@/queries/settings/use-app.query.ts";
import analyticsService, {
  analyticEvents,
  analyticProperties,
} from "@/services/analytics-service.ts";
import { ApiCFTabType, ApiTableTypes } from "@/types/custom-field.types.ts";
import { ApiPermission, EmployeeFieldPermission } from "@/types/role.types.ts";
import { ApiFeatureType } from "@/types/subscription.types.ts";
import { ApiTaskEmployeeRole } from "@/types/task.types.ts";
import { DetailPageLayout, Stack, TabButton, TabList } from "@skailer-com/uikit";
import { parseInt } from "lodash";
import { useEffect, useMemo, useRef } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";
import { twMerge } from "tailwind-merge";
import { useEmployeeProfileAnchor } from "@/hooks/use-employee-profile-anchor.hook.ts";

export function EmployeePage() {
  const { t } = useTranslation();
  const { id } = useParams();
  const { data: employee } = useEmployeeDetails(id ? parseInt(id, 10) : undefined);
  const { data: appConfig } = useAppConfig();
  const { data: employeeMe } = useEmployeeMe();
  const { section } = useParams();
  const navigate = useNavigate();
  const { sideGoalId, isGoalPanelOpen, closeGoalPanel } = useGoalSidePanel();
  const { data: config } = useAppConfig();
  const { renderEmployeeName } = useFormatter();
  const contentPaneRef = useRef<HTMLDivElement | null>(null);
  const railPaneRef = useRef<HTMLDivElement | null>(null);
  useEmployeeProfileAnchor();

  const {
    canViewField,
    canViewAnyOfFields,
    canEditAnyOfFields,
    canIEdit,
    isFeatureEnabled,
    canViewCustomTableForEmployee,
  } = usePermissions();

  useTitle(employee ? renderEmployeeName(employee) : undefined);

  useEffect(() => {
    let tab: string;
    if (section == EmployeePageSection.workInfo) tab = "Work Info";
    else if (section == EmployeePageSection.documents) tab = "Documents";
    else if (section == EmployeePageSection.timeOff) tab = "Time Off";
    else if (section == EmployeePageSection.positionRequests) tab = "Requests";
    else if (section == EmployeePageSection.feedbackEvaluation) tab = "Feedback & Evaluation";
    else if (section == EmployeePageSection.goals) tab = "Goals";
    else if (section == EmployeePageSection.tasks) tab = "Tasks";
    else if (section == EmployeePageSection.tasksRelated) tab = "Tasks Related";
    else tab = "Overview";

    analyticsService.trackEvent(analyticEvents.employees.viewedEmployee, {
      [analyticProperties.id]: id,
      [analyticProperties.source]: "Page",
      [analyticProperties.tab]: tab,
    });
  }, [id, section]);

  useEffect(() => {
    if (section === "notes" && employee) {
      navigate(`/employees/${employee.id}`, { replace: true });
    }
  }, [employee, navigate, section]);

  useEffect(() => {
    // Кит отдаёт ссылки на прокручиваемые колонки, а сам атрибут под useEmployeeProfileAnchor вешает страница
    for (const el of [railPaneRef.current, contentPaneRef.current]) {
      if (el) el.dataset.employeeScrollContainer = "";
    }
  }, [employee]);

  const canViewJobInfo = useMemo(() => {
    const hasFieldPermissions =
      canViewAnyOfFields(employee, [
        EmployeeFieldPermission.employee_positions_history,
        EmployeeFieldPermission.employee_employments_history,
        EmployeeFieldPermission.employee_compensations_history,
        EmployeeFieldPermission.employee_additional_compensation,
      ]) ||
      canEditAnyOfFields(employee, [
        EmployeeFieldPermission.manage_employee_position,
        EmployeeFieldPermission.manage_employee_employment,
        EmployeeFieldPermission.manage_employee_compensation,
        EmployeeFieldPermission.employee_additional_compensation,
      ]);

    const jobInfoTab = config?.ui?.employee_profile?.tabs?.find(
      (tabItem) => tabItem.tab.type === ApiCFTabType.work_info
    );
    const hasCustomTables =
      jobInfoTab?.items?.some(
        (item) =>
          item.table?.table_type === ApiTableTypes.custom &&
          canViewCustomTableForEmployee(employee, item.table.id)
      ) ?? false;

    return hasFieldPermissions || hasCustomTables;
  }, [employee, config, canViewAnyOfFields, canEditAnyOfFields, canViewCustomTableForEmployee]);

  const canViewTimeOff =
    isFeatureEnabled(ApiFeatureType.time_off) &&
    (canIEdit(ApiPermission.manage_leave_policies) ||
      canViewField(employee, EmployeeFieldPermission.employee_time_off));

  const canViewTasks =
    isFeatureEnabled(ApiFeatureType.tasks) && canIEdit(ApiPermission.tasks_module);

  const renderCurrentSection = () => {
    if (!employee) return null;

    const renderStaticContent = () => {
      if (section == EmployeePageSection.documents)
        return <EmployeeDocuments employee={employee} />;
      if (section == EmployeePageSection.timeOff && canViewTimeOff)
        return <EmployeeTimeOff employee={employee} />;
      if (
        section == EmployeePageSection.positionRequests &&
        employeeMe?.id === employee.id &&
        (isFeatureEnabled(ApiFeatureType.ats_hiring_plan) ||
          (isFeatureEnabled(ApiFeatureType.approvals) &&
            isFeatureEnabled(ApiFeatureType.workflows)))
      )
        return <EmployeePositionRequests employee={employee} />;
      if (section == EmployeePageSection.feedbackEvaluation)
        return <EmployeeFeedbackEvaluation employee={employee} />;
      if (section == EmployeePageSection.goals && isFeatureEnabled(ApiFeatureType.goals))
        return <EmployeeGoals employee={employee} />;
      if (section == EmployeePageSection.tasks && canViewTasks)
        return <EmployeeTasks employee={employee} role={ApiTaskEmployeeRole.assignee} />;
      if (section == EmployeePageSection.tasksRelated && canViewTasks)
        return <EmployeeTasks employee={employee} role={ApiTaskEmployeeRole.related} />;
    };

    let customTabType: ApiCFTabType | null;
    if (section === undefined) {
      customTabType = ApiCFTabType.overview;
    } else if (section === EmployeePageSection.workInfo) {
      customTabType = ApiCFTabType.work_info;
    } else {
      customTabType = null;
    }

    return (
      <Stack className={"gap-16"}>
        {renderStaticContent()}
        {customTabType && (
          <EmployeeCustomTabItems
            employee={employee}
            tabType={customTabType}
            tableScrollBoundaryRef={
              section === EmployeePageSection.workInfo ? contentPaneRef : undefined
            }
          />
        )}
      </Stack>
    );
  };

  const canViewRequestsTab =
    employeeMe?.id === employee?.id &&
    (isFeatureEnabled(ApiFeatureType.ats_hiring_plan) ||
      (isFeatureEnabled(ApiFeatureType.approvals) && isFeatureEnabled(ApiFeatureType.workflows)));

  const canViewFeedbackEvaluation = useMemo(() => {
    return (
      (isFeatureEnabled(ApiFeatureType.talent_performance_reviews) &&
        canIEdit(ApiPermission.performance_reviews_module)) ||
      (isFeatureEnabled(ApiFeatureType.talent_surveys) && canIEdit(ApiPermission.surveys_module))
    );
  }, [canIEdit, isFeatureEnabled]);
  const mode = section == undefined ? "full" : "compact";

  const canViewGoals =
    isFeatureEnabled(ApiFeatureType.goals) && canIEdit(ApiPermission.goals_module);

  const variants = {
    full: {
      base: "h-57 transition-all",
    },
    compact: {
      base: "h-24 transition-all",
    },
  };
  const showOnFull = mode == "full" ? "opacity-100" : "opacity-0";
  const showOnCompact = mode == "compact" ? "opacity-100" : "opacity-0";
  const themeVariant = variants[mode];

  const renderEmployeePage = () => {
    if (!employee)
      return <EmployeePageHeaderSkeleton mode={section == undefined ? "full" : "compact"} />;

    return (
      <EmployeePageSidePanelProvider>
        <DetailPageLayout
          contentSize={"md"}
          contentPaneRef={contentPaneRef}
          railPaneRef={railPaneRef}
          heroClassName={themeVariant.base}
          railHeader={<EmployeePageSidePanelHeader employee={employee} />}
          rail={<EmployeePageSidePanelBody employee={employee} />}
          hero={
            <>
              {employee.background_url ? (
                <img
                  src={employee.background_url}
                  alt={t("common:background")}
                  className="w-full h-full object-cover absolute inset-0"
                />
              ) : (
                employee.color_palette &&
                employee.color_palette.length >= 2 && (
                  <GradientContainer colorPalette={employee.color_palette} />
                )
              )}

              <div
                style={{
                  background:
                    "linear-gradient(0deg, var(--color-light) 0%, color-mix(in srgb, var(--color-light) 0%, transparent) 100%)",
                  boxShadow:
                    "0px -1px 0px 0px color-mix(in srgb, var(--color-light) 70%, transparent)",
                }}
                className={twMerge("w-full absolute inset-0 backdrop-blur-sm z-0", showOnCompact)}
              />
            </>
          }
          tabs={
            <>
              <div
                style={{
                  background:
                    "linear-gradient(0deg, var(--color-light) 0%, color-mix(in srgb, var(--color-light) 0%, transparent) 100%)",
                  boxShadow:
                    "0px -1px 0px 0px color-mix(in srgb, var(--color-light) 70%, transparent)",
                }}
                className={twMerge("w-full absolute inset-0 backdrop-blur-sm z-0", showOnFull)}
              />

              <TabList className={"relative z-10"}>
                <TabButton
                  title={t("common:overview")}
                  isActive={!section}
                  onClick={() => {
                    navigate(`/employees/${employee.id}`);
                  }}
                />
                {canViewJobInfo && (
                  <TabButton
                    title={t("core:work_info")}
                    isActive={section == EmployeePageSection.workInfo}
                    onClick={() => {
                      navigate(`/employees/${employee.id}/${EmployeePageSection.workInfo}`);
                    }}
                  />
                )}
                {canViewTimeOff && (
                  <TabButton
                    title={t("time_off:time_off")}
                    isActive={section == EmployeePageSection.timeOff}
                    onClick={() => {
                      navigate(`/employees/${employee.id}/${EmployeePageSection.timeOff}`);
                    }}
                  />
                )}

                {canViewGoals && (
                  <TabButton
                    title={t("goals:goals")}
                    isActive={section == EmployeePageSection.goals}
                    onClick={() => {
                      navigate(`/employees/${employee.id}/${EmployeePageSection.goals}`);
                    }}
                  />
                )}
                {canViewFeedbackEvaluation && (
                  <TabButton
                    title={t("performance_review:feedback_evaluation")}
                    isActive={section == EmployeePageSection.feedbackEvaluation}
                    onClick={() => {
                      navigate(
                        `/employees/${employee.id}/${EmployeePageSection.feedbackEvaluation}`
                      );
                    }}
                  />
                )}
                {employee.can_view_employee_folders ||
                appConfig?.ui.settings.can_view_organization_folders ? (
                  <TabButton
                    title={t("core:documents")}
                    isActive={section == EmployeePageSection.documents}
                    onClick={() => {
                      navigate(`/employees/${employee.id}/${EmployeePageSection.documents}`);
                    }}
                  />
                ) : undefined}

                {canViewTasks && (
                  <TabButton
                    title={t("tasks:tasks_as_assignee")}
                    isActive={section == EmployeePageSection.tasks}
                    onClick={() => {
                      navigate(`/employees/${employee.id}/${EmployeePageSection.tasks}`);
                    }}
                  />
                )}
                {canViewTasks && (
                  <TabButton
                    title={t("tasks:tasks_as_related")}
                    isActive={section == EmployeePageSection.tasksRelated}
                    onClick={() => {
                      navigate(`/employees/${employee.id}/${EmployeePageSection.tasksRelated}`);
                    }}
                  />
                )}
                {canViewRequestsTab && (
                  <TabButton
                    title={t("common:requests")}
                    isActive={section == EmployeePageSection.positionRequests}
                    onClick={() => {
                      navigate(`/employees/${employee.id}/${EmployeePageSection.positionRequests}`);
                    }}
                  />
                )}
              </TabList>
            </>
          }
        >
          {renderCurrentSection()}
        </DetailPageLayout>
      </EmployeePageSidePanelProvider>
    );
  };

  return (
    <RightPaneLayout
      rightPane={
        isGoalPanelOpen && sideGoalId
          ? () => <GoalDetailPanel goalId={sideGoalId} onClose={closeGoalPanel} />
          : undefined
      }
      onHideRightPane={closeGoalPanel}
    >
      {renderEmployeePage()}
    </RightPaneLayout>
  );
}
